import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Pencil } from "lucide-react";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { SidebarPanel } from "../../../components/SidebarPanel";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { ManageAddonsModal } from "./ManageAddonsModal";
import { BillingItemEditForm } from "./BillingItemEditForm";
import { BillingItemMonthlyPrice } from "./BillingItemMonthlyPrice";
import { useUpdateBillingItemMutation } from "../../../query/billingItemsQuery";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatDate, toDateString } from "../../../utils/dateUtils";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { RestAvailableBillingItem } from "@KassaroAB/shared/types/availableBillingItem";
import {
  getCurrentOrNextPositiveSegment,
  isBillingItemCurrentOrFuture,
} from "../../../utils/billingItemSegments";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const AddonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const AddonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  width: 100%;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};
`;

const AddonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  min-width: 0;
`;

const AddonName = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const AddonMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const AddonActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

const Badge = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  padding: ${spacing.x05}px ${spacing.x1}px;
  margin-left: ${spacing.x1}px;
  border-radius: 4px;
  background-color: ${colors.infoBackground};
  color: ${colors.info};
`;

interface ActiveAddonsSectionProps {
  billingItems: RestBillingItem[] | undefined;
  availableBillingItems: RestAvailableBillingItem[] | undefined;
  isLoading?: boolean;
  billingPeriod: BillingPeriod;
  startDate: string;
}

export const ActiveAddonsSection = ({
  billingItems,
  availableBillingItems,
  isLoading = false,
  billingPeriod,
  startDate,
}: ActiveAddonsSectionProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const today = toDateString(new Date());
  const [isManageAddonsOpen, setIsManageAddonsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RestBillingItem | null>(null);
  const { mutate: updateBillingItem, isPending: isUpdating } =
    useUpdateBillingItemMutation(storeId ?? null);

  const lookup = new Map((availableBillingItems || []).map((item) => [item.id, item]));
  const displayedAddonItems = (billingItems ?? [])
    .filter(
      (billingItem) =>
        billingItem.type === BillingItemType.ADD_ON &&
        isBillingItemCurrentOrFuture(billingItem, today)
    )
    .sort((left, right) => left.startDate.localeCompare(right.startDate));
  const selectedAvailable = selectedItem
    ? lookup.get(selectedItem.availableBillingItemId)
    : undefined;

  return (
    <Card>
      <SectionHeader>
        <SectionTitle>{t("packageSelection.addons")}</SectionTitle>
        <Button onClick={() => setIsManageAddonsOpen(true)}>
          {t("packageSelection.addAddons")}
        </Button>
      </SectionHeader>

      {isLoading ? (
        <LoadingContainer message={t("billingItems.loading")} />
      ) : displayedAddonItems.length === 0 ? (
        <EmptyText>{t("packageSelection.noActiveAddons")}</EmptyText>
      ) : (
        <AddonList>
          {displayedAddonItems.map((billingItem) => {
            const availableItem = lookup.get(billingItem.availableBillingItemId);
            const monthlyPrice =
              availableItem?.pricing?.monthlyPrice?.currencies?.[Currency.SEK];
            const displaySegment = getCurrentOrNextPositiveSegment(
              billingItem,
              today
            );
            const isUpcoming =
              (displaySegment?.startDate ?? billingItem.startDate) > today;
            return (
              <AddonRow key={billingItem.id}>
                <AddonInfo>
                  <AddonName>
                    {availableItem?.name ?? billingItem.availableBillingItemId}
                    {isUpcoming && (
                      <Badge>{t("billingItems.status.upcoming")}</Badge>
                    )}
                  </AddonName>
                  <AddonMeta>
                    {t(
                      `billingItems.billingPeriod.${
                        billingItem.billingPeriod ?? BillingPeriod.MONTHLY
                      }`
                    )}
                    {" · "}
                    {formatDate(displaySegment?.startDate ?? billingItem.startDate)}
                    {" – "}
                    {displaySegment?.endDate
                      ? formatDate(displaySegment.endDate)
                      : t("entitlements.ongoing")}
                  </AddonMeta>
                </AddonInfo>
                <AddonActions>
                  <BillingItemMonthlyPrice
                    unitPrice={monthlyPrice}
                    discount={billingItem.discount}
                  />
                  <IconButton
                    type="button"
                    aria-label={t("packageSelection.editDetails")}
                    onClick={() => setSelectedItem(billingItem)}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </AddonActions>
              </AddonRow>
            );
          })}
        </AddonList>
      )}

      <ManageAddonsModal
        isOpen={isManageAddonsOpen}
        onClose={() => setIsManageAddonsOpen(false)}
        billingItems={billingItems}
        defaultBillingPeriod={billingPeriod}
        defaultStartDate={startDate}
      />
      {selectedItem && (
        <SidebarPanel
          isOpen
          onClose={() => setSelectedItem(null)}
          title={
            selectedAvailable?.name ?? selectedItem.availableBillingItemId
          }
        >
          <BillingItemEditForm
            billingItem={selectedItem}
            pricing={
              selectedAvailable?.pricing
                ? {
                    monthlyPrice: selectedAvailable.pricing.monthlyPrice,
                    prepaid36MonthPrice:
                      selectedAvailable.pricing.prepaid36MonthPrice,
                    oneTimePrice: selectedAvailable.pricing.oneTimePrice,
                  }
                : undefined
            }
            onSubmit={(data) => {
              updateBillingItem(
                { billingItemId: selectedItem.id, billingItem: data },
                { onSuccess: () => setSelectedItem(null) }
              );
            }}
            onCancel={() => setSelectedItem(null)}
            isSubmitting={isUpdating}
          />
        </SidebarPanel>
      )}
    </Card>
  );
};
